import { Component } from '@theme/component';

/**
 * Gourmet Pantry recipe page (sections/gp-article-main.liquid).
 *
 * The ingredient and step checklists are native checkboxes styled with CSS, so they work
 * without this script. This component adds what needs JavaScript: the Print and Share
 * buttons (hidden until it runs) and the step progress label and bar.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} [printButton]
 * @property {HTMLButtonElement} [shareButton]
 * @property {HTMLElement} [status] - Polite status message for "Link copied".
 * @property {HTMLElement} [progress] - Progress wrapper, hidden until this runs.
 * @property {HTMLElement} [progressLabel]
 * @property {HTMLElement} [progressBar]
 * @property {HTMLInputElement[]} [stepInputs]
 *
 * @extends {Component<Refs>}
 */
class GpRecipeComponent extends Component {
  /** @type {number | undefined} */
  #statusTimeout;

  connectedCallback() {
    super.connectedCallback();
    this.#enhance();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#enhance();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#statusTimeout);
  }

  #enhance() {
    const { printButton, shareButton, progress } = this.refs;
    if (printButton) printButton.hidden = false;
    if (shareButton) shareButton.hidden = false;
    if (progress) progress.hidden = false;
    this.handleStepChange();
  }

  handlePrint() {
    window.print();
  }

  async handleShare() {
    const url = window.location.href;
    const title = this.refs.shareButton?.dataset.title ?? document.title;

    try {
      if (navigator.share) {
        await navigator.share({ title, url });
        return;
      }
      await navigator.clipboard.writeText(url);
      this.#showStatus(this.dataset.copied ?? '');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.warn('[gp-recipe] Share failed', error);
    }
  }

  /** Updates the step progress label and bar. */
  handleStepChange() {
    const steps = this.refs.stepInputs ?? [];
    const { progressLabel, progressBar } = this.refs;
    if (!steps.length) return;

    const done = steps.filter((input) => input.checked).length;
    const total = steps.length;

    if (progressBar) progressBar.style.width = `${(done / total) * 100}%`;
    if (progressLabel) {
      const template = done === total ? this.dataset.progressDone : this.dataset.progressTemplate;
      progressLabel.textContent = (template ?? '').replace('[done]', String(done)).replace('[total]', String(total));
    }
  }

  /** @param {string} message */
  #showStatus(message) {
    const { status } = this.refs;
    if (!status || !message) return;

    status.textContent = message;
    clearTimeout(this.#statusTimeout);
    this.#statusTimeout = setTimeout(() => {
      status.textContent = '';
    }, 2500);
  }
}

if (!customElements.get('gp-recipe-component')) {
  customElements.define('gp-recipe-component', GpRecipeComponent);
}
